import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { log } from "../lib/logger";
import { useAuth } from "../contexts/AuthContext";
import NotificationItem from "./notifications/NotificationItem";

// Poll interval (ms) for unread count. Kept modest — bell only refreshes when user is idle on a page.
const POLL_MS = 45_000;

export default function NotificationBell() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const reload = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/notifications/me");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch (err) {
      log.warn("[NotificationBell] reload failed:", err);
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    reload();
    const id = setInterval(reload, POLL_MS);
    return () => clearInterval(id);
  }, [user, reload]);

  // Click outside to close
  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const markRead = async (id) => {
    try { await api.post(`/notifications/${id}/read`); }
    catch (err) { log.warn("[NotificationBell] mark-read failed:", err); }
    setItems((prev) => prev.map((n) => n.notification_id === id ? { ...n, read: true } : n));
    setUnread((u) => Math.max(0, u - 1));
  };
  const markAll = async () => {
    try { await api.post("/notifications/read-all"); }
    catch (err) { log.warn("[NotificationBell] mark-all failed:", err); }
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnread(0);
  };

  const onSelect = (id) => { markRead(id); setOpen(false); };

  if (!user) return null;

  return (
    <div ref={wrapRef} className="relative" data-testid="notification-bell">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label={`Notifications${unread ? ` (${unread} unread)` : ""}`}
        className="relative w-9 h-9 flex items-center justify-center rounded-full border border-white/15 hover:border-gold hover:text-gold transition-colors"
        data-testid="notification-bell-btn"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 8a6 6 0 1 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
          <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
        </svg>
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-gold text-black text-[10px] leading-[18px] font-medium text-center"
            data-testid="notification-unread-count"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[360px] max-h-[80vh] overflow-y-auto glass-strong border border-white/10 rounded-lg shadow-2xl z-50" data-testid="notification-panel">
          <div className="flex items-center justify-between p-3 border-b border-white/10">
            <div className="overline text-gold">Notifications</div>
            {unread > 0 && (
              <button onClick={markAll} className="text-[10px] uppercase tracking-widest text-white/50 hover:text-gold" data-testid="notification-mark-all">
                Mark all read
              </button>
            )}
          </div>
          {items.length === 0 ? (
            <div className="p-6 text-center text-sm text-white/40">No notifications yet.</div>
          ) : (
            items.map((n) => (
              <NotificationItem key={n.notification_id} n={n} onSelect={onSelect} />
            ))
          )}
        </div>
      )}
    </div>
  );
}
